import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Camera, CheckCircle2, ImagePlus, QrCode, X } from 'lucide-react';
import { animalsApi, attachmentsApi } from '../../services/resources';
import { registerCustomForm } from '../../features/common/formRegistry';
import { useFormRule } from '../../features/common/formSettings';
import { getApiErrorMessage } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { useReferenceData } from '../../features/common/referenceData';
import { ILMSPageHeader } from '../../components/ILMSPageHeader';
import { ILMSCard } from '../../components/ILMSCard';
import { ILMSButton } from '../../components/ILMSButton';
import { ILMSAlert } from '../../components/ILMSAlert';
import { ILMSFormSection, ILMSFormField, ILMSInput, ILMSSelect } from '../../components/ILMSForm';
import { ILMSDatePicker } from '../../components/ILMSDatePicker';
import { ILMSSpinner } from '../../components/ILMSLoadingState';
import type { Animal, AnimalCreate, AnimalHealthStatus, AnimalProductionStatus, AnimalSource, AnimalUpdate, Gender } from '../../types';

const MIN_PHOTOS = 4;

interface FormValues {
  farm_id: string;
  animal_type_id: string;
  breed_id: string;
  animal_name: string;
  gender: Gender;
  date_of_birth: string;
  color: string;
  source: AnimalSource;
  health_status: AnimalHealthStatus;
  production_status: AnimalProductionStatus;
  current_weight_kg: string;
  animal_id: string; // business code
  asset_type_id: string;
  asset_code: string;
  location_id: string;
  tag_id: string;
}

const emptyValues: FormValues = {
  farm_id: '',
  animal_type_id: '',
  breed_id: '',
  animal_name: '',
  gender: 'Female',
  date_of_birth: '',
  color: '',
  source: 'BornInFarm',
  health_status: 'Active',
  production_status: 'Dry',
  current_weight_kg: '',
  animal_id: '',
  asset_type_id: '',
  asset_code: '',
  location_id: '',
  tag_id: '',
};

// Optional on this form unless made required on Administration -> Entry Form Settings.
const ANIMAL_OPTIONAL_FIELDS: [keyof FormValues, string][] = [
  ['breed_id', 'Breed'], ['animal_name', 'Name'], ['date_of_birth', 'Date of Birth'], ['color', 'Color / Markings'],
  ['location_id', 'Current Location'], ['current_weight_kg', 'Current Weight (kg)'],
];

registerCustomForm({
  id: 'animal-form', page: 'Animals', title: 'Add / Edit Animal', limited: true,
  fields: [
    ...[['animal_type_id', 'Animal Type'], ['gender', 'Gender'], ['farm_id', 'Farm'], ['tag_id', 'Tag ID'], ['source', 'Source']]
      .map(([name, label]) => ({ name, label, type: 'text' as const, section: '', required: true })),
    ...ANIMAL_OPTIONAL_FIELDS.map(([name, label]) => ({ name, label, type: 'text' as const, section: '', optional: true })),
  ],
  options: [{ key: 'min_photos', label: 'Minimum photos when adding an animal', min: 0, max: 10, defaultValue: MIN_PHOTOS, help: '0 = photos are optional.' }],
});

export default function AnimalForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Reached from Procurement & Finance > Purchases' "+ Add new animal"
  // action (Type=Animal, no matching animal yet) — after saving, the most
  // useful next step isn't this animal's own profile, it's going straight
  // back to record what it cost.
  const returnToPurchases = searchParams.get('returnTo') === 'purchases';
  const queryClient = useQueryClient();
  const { can } = useAuth();
  // Purchase date/acquisition cost are commercially sensitive — only
  // whoever can edit the Purchases module (Super Admin/Admin) sees or
  // sets them; everyone else has the fields hidden entirely, on both the
  // read side (backend already blanks them out) and here on the write
  // side, so a non-privileged edit to some other field can't accidentally
  // null out a cost it never saw in the first place.
  const canViewCost = can('Purchases', 'Edit');
  const formRule = useFormRule('animal-form');
  const minPhotos = Math.max(0, Number(formRule.options?.min_photos ?? MIN_PHOTOS));
  const mustFill = (name: keyof FormValues) => !!formRule.fields?.[name]?.required;

  const animalQuery = useQuery({
    queryKey: ['animal', id],
    queryFn: () => animalsApi.get(id!),
    enabled: isEdit,
  });

  const { data: refData, isLoading: refLoading } = useReferenceData(['animalTypes', 'breeds', 'locations', 'farms', 'assetTypes']);

  const [values, setValues] = useState<FormValues>(() =>
    returnToPurchases ? { ...emptyValues, source: 'Purchased' } : emptyValues
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [created, setCreated] = useState<Animal | null>(null);
  const [photos, setPhotos] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const [photoUploadState, setPhotoUploadState] = useState<{ uploading: boolean; failedCount: number }>({ uploading: false, failedCount: 0 });
  const photoInputRef = useRef<HTMLInputElement>(null);

  const addPhotos = (files: FileList | null) => {
    if (!files) return;
    const next = Array.from(files);
    setPhotos((prev) => [...prev, ...next]);
    setPhotoPreviews((prev) => [...prev, ...next.map((f) => URL.createObjectURL(f))]);
    setErrors((prev) => ({ ...prev, photos: '' }));
  };

  const removePhoto = (index: number) => {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
    setPhotoPreviews((prev) => {
      URL.revokeObjectURL(prev[index]);
      return prev.filter((_, i) => i !== index);
    });
  };

  useEffect(() => {
    return () => {
      photoPreviews.forEach((url) => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (animalQuery.data) {
      const a = animalQuery.data;
      setValues((prev) => ({
        ...prev,
        animal_type_id: a.animal_type_id,
        breed_id: a.breed_id ?? '',
        animal_name: a.animal_name ?? '',
        gender: a.gender,
        date_of_birth: a.date_of_birth ?? '',
        color: a.color ?? '',
        source: a.source,
        health_status: a.health_status,
        production_status: a.production_status,
        current_weight_kg: a.current_weight_kg ?? '',
        animal_id: a.animal_id,
        asset_code: a.asset_code ?? '',
        location_id: a.location_id ?? '',
      }));
    }
  }, [animalQuery.data]);

  useEffect(() => {
    // Default to the only farm when there's exactly one — most farm deployments have one.
    if (!isEdit && !values.farm_id && refData.farms.length === 1) {
      setValues((prev) => ({ ...prev, farm_id: refData.farms[0].value }));
    }
    if (!isEdit && !values.asset_type_id && refData.assetTypes.length > 0) {
      const animalAssetType = refData.assetTypes.find((t) => t.label.toLowerCase() === 'animal');
      setValues((prev) => ({ ...prev, asset_type_id: (animalAssetType ?? refData.assetTypes[0]).value }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refData.farms, refData.assetTypes]);

  const set = <K extends keyof FormValues>(name: K, value: FormValues[K]) => setValues((prev) => ({ ...prev, [name]: value }));
  // Required/optional marker and error for a field the admin can make required.
  const need = (name: keyof FormValues) => (mustFill(name) ? { required: true, error: errors[name] } : { optional: true, error: errors[name] });

  const mutation = useMutation({
    mutationFn: async () => {
      if (isEdit) {
        const payload: AnimalUpdate = {
          animal_type_id: values.animal_type_id || undefined,
          breed_id: values.breed_id || null,
          animal_name: values.animal_name || null,
          gender: values.gender,
          date_of_birth: values.date_of_birth || null,
          color: values.color || null,
          source: values.source,
          health_status: values.health_status,
          production_status: values.production_status,
          current_weight_kg: values.current_weight_kg === '' ? null : Number(values.current_weight_kg),
          location_id: values.location_id || null,
        };
        return animalsApi.update(id!, payload);
      }
      const payload: AnimalCreate = {
        farm_id: values.farm_id,
        animal_type_id: values.animal_type_id,
        breed_id: values.breed_id || null,
        animal_name: values.animal_name || null,
        gender: values.gender,
        date_of_birth: values.date_of_birth || null,
        color: values.color || null,
        source: values.source,
        health_status: values.health_status,
        production_status: values.production_status,
        current_weight_kg: values.current_weight_kg === '' ? null : Number(values.current_weight_kg),
        animal_id: values.animal_id || null,
        asset_type_id: values.asset_type_id || null,
        asset_code: values.asset_code || null,
        location_id: values.location_id || null,
        tag_id: values.tag_id.trim(),
      };
      return animalsApi.create(payload);
    },
    onSuccess: async (animal) => {
      queryClient.invalidateQueries({ queryKey: ['animals'] });
      // The animal pickers on every other form (Purchases, Milk, Breeding...)
      // read a cached list - without this a just-added animal is missing
      // from them for up to 5 minutes.
      queryClient.invalidateQueries({ queryKey: ['reference', 'animals'] });
      if (isEdit) {
        navigate(`/animals/${animal.id}`);
        return;
      }
      // The animal must exist before photos can be attached to it (Attachments
      // are keyed by entity_id) — so photos upload right after creation, not
      // before. The animal record is never lost even if some uploads fail;
      // the profile's Attachments tab lets the operator retry those.
      setPhotoUploadState({ uploading: true, failedCount: 0 });
      let failedCount = 0;
      for (const photo of photos) {
        try {
          await attachmentsApi.upload(photo, 'Animal', animal.id);
        } catch {
          failedCount += 1;
        }
      }
      setPhotoUploadState({ uploading: false, failedCount });
      setCreated(animal);
    },
    onError: (err) => {
      if (axios.isAxiosError(err) && err.response?.status === 422) {
        const detail = err.response.data?.detail;
        if (Array.isArray(detail)) {
          const next: Record<string, string> = {};
          detail.forEach((d: { loc?: string[]; msg?: string }) => {
            const field = d.loc?.[d.loc.length - 1];
            if (field) next[field] = d.msg ?? 'This value is not valid.';
          });
          setErrors(next);
        }
      }
      setFormError(getApiErrorMessage(err, 'Unable to save this animal. Please try again.'));
    },
  });

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!isEdit && !values.farm_id) next.farm_id = 'Farm is required.';
    if (!isEdit && !values.tag_id.trim()) next.tag_id = 'Tag ID is required — every animal needs a unique ear tag/RFID number.';
    if (!isEdit && photos.length < minPhotos) next.photos = `Please add at least ${minPhotos} photos of the animal.`;
    // Made required on Administration -> Entry Form Settings.
    for (const [name, label] of ANIMAL_OPTIONAL_FIELDS) {
      if (mustFill(name) && !String(values[name] ?? '').trim()) next[name] = `Please fill in ${label}.`;
    }
    if (!values.animal_type_id) next.animal_type_id = 'Animal type is required.';
    if (!values.gender) next.gender = 'Gender is required.';
    if (!values.source) next.source = 'Source is required.';
    if (values.date_of_birth && values.date_of_birth > new Date().toISOString().slice(0, 10)) {
      next.date_of_birth = 'Date of birth cannot be in the future.';
    }
    if (values.current_weight_kg !== '' && Number(values.current_weight_kg) <= 0) {
      next.current_weight_kg = 'Weight must be greater than 0.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!validate()) {
      setFormError('Please fix the highlighted fields before saving.');
      return;
    }
    mutation.mutate();
  };

  if (isEdit && animalQuery.isLoading) {
    return <ILMSSpinner label="Loading animal…" />;
  }

  if (isEdit && animalQuery.isError) {
    return (
      <ILMSAlert tone="critical" title="Unable to load this animal">
        {getApiErrorMessage(animalQuery.error)}
      </ILMSAlert>
    );
  }

  if (created) {
    return (
      <div className="max-w-lg mx-auto">
        <ILMSCard>
          <div className="flex flex-col items-center text-center py-4">
            <div className="h-14 w-14 rounded-full bg-success-50 text-success-600 flex items-center justify-center mb-3">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <h2 className="text-section-title">Animal added successfully</h2>
            <div className="mt-3 w-full text-left bg-surface-subtle rounded-md p-3 space-y-1">
              <p className="text-secondary text-ink-500">
                Animal ID: <span className="font-semibold text-ink-900">{created.animal_id}</span>
              </p>
              <p className="text-secondary text-ink-500">
                Asset Code: <span className="font-semibold text-ink-900">{created.asset_code ?? '—'}</span>
              </p>
              <p className="text-secondary text-ink-500 flex items-center gap-1.5">
                <QrCode className="h-4 w-4" /> QR code generated successfully.
              </p>
              <p className="text-secondary text-ink-500 flex items-center gap-1.5">
                <Camera className="h-4 w-4" />
                {photoUploadState.failedCount === 0
                  ? `${photos.length} photo${photos.length === 1 ? '' : 's'} uploaded.`
                  : `${photos.length - photoUploadState.failedCount} of ${photos.length} photos uploaded — ${photoUploadState.failedCount} failed. Add the rest from the animal's profile.`}
              </p>
            </div>
            {photoUploadState.failedCount > 0 && (
              <ILMSAlert tone="warning" className="mt-3 w-full text-left">
                Some photos failed to upload. Open the animal's profile and use its Attachments tab to add them again.
              </ILMSAlert>
            )}
            {returnToPurchases ? (
              <div className="flex flex-col gap-2 mt-5 w-full">
                <ILMSButton fullWidth onClick={() => navigate(`/procurement-finance?tab=purchases&prefill_animal=${created.id}`)}>
                  Continue — record its purchase cost
                </ILMSButton>
                <ILMSButton variant="ghost" fullWidth onClick={() => navigate(`/animals/${created.id}`)}>
                  View Animal instead
                </ILMSButton>
              </div>
            ) : (
              <div className="flex flex-col gap-2 mt-5 w-full">
              {created.source === 'Purchased' && canViewCost && (
                <ILMSButton fullWidth onClick={() => navigate(`/procurement-finance?tab=purchases&prefill_animal=${created.id}`)}>
                  Record its purchase price now
                </ILMSButton>
              )}
              <div className="flex gap-2 w-full">
                <ILMSButton variant="outline" fullWidth onClick={() => navigate(`/animals/${created.id}`)}>
                  View Animal
                </ILMSButton>
                <ILMSButton
                  fullWidth
                  onClick={() => {
                    setCreated(null);
                    setValues(emptyValues);
                    setErrors({});
                    photoPreviews.forEach((url) => URL.revokeObjectURL(url));
                    setPhotos([]);
                    setPhotoPreviews([]);
                    setPhotoUploadState({ uploading: false, failedCount: 0 });
                  }}
                >
                  Add Another
                </ILMSButton>
              </div>
              </div>
            )}
          </div>
        </ILMSCard>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 max-w-4xl">
      <ILMSPageHeader
        title={isEdit ? 'Edit Animal' : 'Add Animal'}
        subtitle={isEdit ? `Editing ${values.animal_id || ''}` : 'Enter the animal’s details below. Fields marked * are required.'}
      />

      <ILMSCard>
        <form onSubmit={handleSubmit} className="flex flex-col gap-6" noValidate>
          {formError && <ILMSAlert tone="critical">{formError}</ILMSAlert>}

          {!isEdit && (
            <ILMSFormSection title="Photos" description={minPhotos === 0 ? 'Photos are optional - add as many as you like.' : `Add at least ${minPhotos} photos of the animal (e.g. front, side, both flanks, identifying marks).`}>
              <div className="sm:col-span-full flex flex-col gap-3">
                <div className="flex flex-wrap gap-3">
                  {photoPreviews.map((src, i) => (
                    <div key={src} className="relative h-24 w-24 rounded-md overflow-hidden border border-ink-300/20 shrink-0">
                      <img src={src} alt={`Animal photo ${i + 1}`} className="h-full w-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removePhoto(i)}
                        aria-label="Remove photo"
                        className="absolute top-1 right-1 h-6 w-6 rounded-full bg-ink-900/70 text-white flex items-center justify-center hover:bg-critical-600"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => photoInputRef.current?.click()}
                    className="h-24 w-24 rounded-md border-2 border-dashed border-ink-300/40 flex flex-col items-center justify-center gap-1 text-ink-400 hover:border-brand-green-400 hover:text-brand-green-600 shrink-0"
                  >
                    <ImagePlus className="h-5 w-5" />
                    <span className="text-helper-text">Add</span>
                  </button>
                </div>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  capture="environment"
                  className="hidden"
                  onChange={(e) => {
                    addPhotos(e.target.files);
                    e.target.value = '';
                  }}
                />
                <p className={`text-helper-text flex items-center gap-1.5 ${errors.photos ? 'text-critical-600' : 'text-ink-400'}`}>
                  <Camera className="h-3.5 w-3.5" />
                  {photos.length} of {minPhotos} minimum photos added
                  {errors.photos ? ` — ${errors.photos}` : ''}
                </p>
              </div>
            </ILMSFormSection>
          )}

          <ILMSFormSection title="Basic Information" description="What kind of animal is this?">
            <ILMSFormField label="Animal Type" htmlFor="animal_type_id" required error={errors.animal_type_id}>
              <ILMSSelect id="animal_type_id" value={values.animal_type_id} error={!!errors.animal_type_id} onChange={(e) => set('animal_type_id', e.target.value)}>
                <option value="">Select animal type…</option>
                {refData.animalTypes.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </ILMSSelect>
            </ILMSFormField>
            <ILMSFormField label="Breed" htmlFor="breed_id" {...need('breed_id')}>
              <ILMSSelect id="breed_id" value={values.breed_id} error={!!errors.breed_id} onChange={(e) => set('breed_id', e.target.value)}>
                <option value="">Select breed…</option>
                {refData.breeds.map((b) => (
                  <option key={b.value} value={b.value}>{b.label}</option>
                ))}
              </ILMSSelect>
            </ILMSFormField>
            <ILMSFormField label="Name" htmlFor="animal_name" {...need('animal_name')}>
              <ILMSInput id="animal_name" value={values.animal_name} onChange={(e) => set('animal_name', e.target.value)} placeholder="e.g. Lakshmi" />
            </ILMSFormField>
            <ILMSFormField label="Gender" htmlFor="gender" required error={errors.gender}>
              <ILMSSelect id="gender" value={values.gender} error={!!errors.gender} onChange={(e) => set('gender', e.target.value as Gender)}>
                <option value="Female">Female</option>
                <option value="Male">Male</option>
              </ILMSSelect>
            </ILMSFormField>
            <ILMSFormField label="Date of Birth" htmlFor="date_of_birth" {...need('date_of_birth')}>
              <ILMSDatePicker id="date_of_birth" value={values.date_of_birth} error={errors.date_of_birth} onChange={(e) => set('date_of_birth', e.target.value)} />
            </ILMSFormField>
            <ILMSFormField label="Color / Markings" htmlFor="color" {...need('color')}>
              <ILMSInput id="color" value={values.color} onChange={(e) => set('color', e.target.value)} placeholder="e.g. Black & white" />
            </ILMSFormField>
          </ILMSFormSection>

          <ILMSFormSection title="Identification & Location" description="How this animal is tracked and where it's kept.">
            {!isEdit && (
              <ILMSFormField label="Farm" htmlFor="farm_id" required error={errors.farm_id}>
                <ILMSSelect id="farm_id" value={values.farm_id} error={!!errors.farm_id} onChange={(e) => set('farm_id', e.target.value)}>
                  <option value="">Select farm…</option>
                  {refData.farms.map((f) => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </ILMSSelect>
              </ILMSFormField>
            )}
            <ILMSFormField label="Animal ID" htmlFor="animal_id" optional helperText="Leave blank to auto-generate a business code.">
              <ILMSInput id="animal_id" value={values.animal_id} disabled={isEdit} onChange={(e) => set('animal_id', e.target.value)} placeholder="e.g. COW-0142" />
            </ILMSFormField>
            {!isEdit ? (
              <ILMSFormField label="Tag ID" htmlFor="tag_id" required error={errors.tag_id}
                helperText="The physical ear tag / RFID number — must be unique, this is what stops the same animal being entered twice.">
                <ILMSInput id="tag_id" value={values.tag_id} error={!!errors.tag_id} onChange={(e) => set('tag_id', e.target.value)} placeholder="e.g. IN-EARTAG-0142" />
              </ILMSFormField>
            ) : (
              <ILMSFormField label="Tag ID" htmlFor="tag_id_display" optional
                helperText="To add or correct a tag, use the Identification tab on this animal's profile.">
                <ILMSInput id="tag_id_display" value={animalQuery.data?.primary_tag || 'No tag assigned'} disabled readOnly />
              </ILMSFormField>
            )}
            {!isEdit && (
              <ILMSFormField label="Asset Code" htmlFor="asset_code" optional helperText="Leave blank to auto-generate.">
                <ILMSInput id="asset_code" value={values.asset_code} onChange={(e) => set('asset_code', e.target.value)} />
              </ILMSFormField>
            )}
            <ILMSFormField label="Current Location" htmlFor="location_id" {...need('location_id')}>
              <ILMSSelect id="location_id" value={values.location_id} error={!!errors.location_id} onChange={(e) => set('location_id', e.target.value)}>
                <option value="">Select location…</option>
                {refData.locations.map((l) => (
                  <option key={l.value} value={l.value}>{l.label}</option>
                ))}
              </ILMSSelect>
            </ILMSFormField>
          </ILMSFormSection>

          <ILMSFormSection title="Source & Acquisition">
            <ILMSFormField label="Source" htmlFor="source" required error={errors.source}>
              <ILMSSelect id="source" value={values.source} error={!!errors.source} onChange={(e) => set('source', e.target.value as AnimalSource)}>
                <option value="BornInFarm">Born on farm</option>
                <option value="Purchased">Purchased</option>
                <option value="Transferred">Transferred</option>
              </ILMSSelect>
            </ILMSFormField>
            {values.source === 'Purchased' && canViewCost && (
              <div className="sm:col-span-2">
                <ILMSAlert tone="info">
                  {isEdit && animalQuery.data?.acquisition_cost != null
                    ? `Purchase cost recorded: ₹${animalQuery.data.acquisition_cost} on ${animalQuery.data.purchase_date}. To add or correct it, use Finance → Animal Purchases — that's the one place this animal's purchase cost is entered.`
                    : 'Purchase cost isn’t entered here. After saving, record it once under Finance → Animal Purchases (with supplier, invoice, transport/insurance cost) — this animal’s profile will pick it up automatically.'}
                </ILMSAlert>
              </div>
            )}
            <ILMSFormField label="Current Weight (kg)" htmlFor="current_weight_kg" {...need('current_weight_kg')}>
              <ILMSInput id="current_weight_kg" type="number" min={0} step={0.1} value={values.current_weight_kg} error={!!errors.current_weight_kg} onChange={(e) => set('current_weight_kg', e.target.value)} />
            </ILMSFormField>
          </ILMSFormSection>

          <ILMSFormSection title="Status">
            <ILMSFormField label="Health Status" htmlFor="health_status" required>
              <ILMSSelect id="health_status" value={values.health_status} onChange={(e) => {
                set('health_status', e.target.value as AnimalHealthStatus);
                // Returned to the supplier: production follows (still changeable below).
                if (e.target.value === 'Returned') set('production_status', 'Returned');
              }}>
                <option value="Active">Active</option>
                <option value="Quarantine">Quarantine</option>
                <option value="Hospitalized">Hospitalized</option>
                <option value="Sold">Sold</option>
                <option value="Returned">Returned to Supplier</option>
                <option value="Dead">Dead</option>
                <option value="Disposed">Disposed</option>
              </ILMSSelect>
            </ILMSFormField>
            <ILMSFormField label="Production Status" htmlFor="production_status" required>
              <ILMSSelect id="production_status" value={values.production_status} onChange={(e) => set('production_status', e.target.value as AnimalProductionStatus)}>
                <option value="Dry">Dry</option>
                <option value="Pregnant">Pregnant</option>
                <option value="Lactating">Lactating</option>
                <option value="PregnantLactating">Pregnant &amp; Milking</option>
                <option value="PregnantDry">Pregnant &amp; Dry</option>
                <option value="Sick">Sick</option>
                <option value="Sold">Sold</option>
                <option value="Returned">Returned to Supplier</option>
                <option value="Dead">Dead</option>
              </ILMSSelect>
            </ILMSFormField>
          </ILMSFormSection>

          <div className="flex justify-end gap-2 pt-2 border-t border-ink-300/10">
            <ILMSButton type="button" variant="ghost" onClick={() => navigate(-1)}>
              Cancel
            </ILMSButton>
            <ILMSButton type="submit" loading={mutation.isPending || photoUploadState.uploading} disabled={refLoading}>
              {photoUploadState.uploading ? 'Uploading photos…' : isEdit ? 'Save changes' : 'Add Animal'}
            </ILMSButton>
          </div>
        </form>
      </ILMSCard>
    </div>
  );
}
